import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import queryString from "query-string";
import { Header, Icon, Segment, Accordion } from "semantic-ui-react";
import "./InspectionPanel.scss";
import { faMessage } from "@fortawesome/pro-regular-svg-icons";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { faMapLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useInspection from "../../hooks/inspections/useInspection";
import InspectionStaffLocationTabChangeLocationModal from "./InspectionPanel/components/InspectionStaffLocationTabChangeLocationModal";
import useCoreLocation from "./InspectionPanel/useCoreLocation";
import { InspectionStatus } from "./citizen-inspection-list/constants/InspectionStatus";
import useCreateInspectionStartedAt from "../../hooks/inspections/useInspectionStartedUpdate";
import InspectionPanelButton from "./InspectionPanel/components/InspectionPanelButton";
import InspectionPanelEllipsisPopup from "./InspectionPanel/components/InspectionPanelEllipsisDropdown";
import InspectionPanelOnSiteContactInfo from "./InspectionPanel/components/InspectionPanelOnSiteContactInfo";
import ScheduleInspectionModal from "./ScheduleInspectionModal/ScheduleInspectionModal";
import InspectionPanelCompletedSegment from "./InspectionPanel/components/InspectionPanelCompletedSegment";
import useInspectionPanelOnSiteContactInfo from "./InspectionPanel/useInspectionPanelOnSiteContactInfo";
import { InspectionChecklistUtils } from "../../routes/InspectionChecklist";
import InspectionPanelSectionsNavigation from "./InspectionPanel/components/InspectionPanelSectionsNavigation";
import InspectionDeleteModal from "./InspectionPanel/components/InspectionDeleteModal";
import useInspectionsListUrl from "./InspectionPanel/useInspectionsListUrl";
import useLocationUrl from "./InspectionPanel/useLocationUrl";
import InspectionScheduledDateDisplay from "./InspectionScheduledDateDisplay";
import permissionsSelectors from "../../../../selectors/permissions";

export const InspectionPanel = ({
  id,
  inspectionTitle,
  openModal,
  permitsTypeName,
  address,
  applicant,
  confirmationNumber,
  recordId,
  site,
  assignee,
  scheduledAt,
}: any) => {
  const { inspectionQuery } = useInspection({ id: id });

  const { createInspectionStartedAt, isLoading: isStartedAtLoading } = useCreateInspectionStartedAt({ id: id });

  const primaryLocationId = inspectionQuery.data?.data.attributes.primaryLocationId;
  const status = inspectionQuery.data?.data.attributes.status as InspectionStatus;

  const onSiteContactInfo = useInspectionPanelOnSiteContactInfo({ id: id });

  const { data } = useCoreLocation({ id: primaryLocationId });
  const locationId = data?.id;
  const parcelNumber = data?.parcelNumber;
  const history = useHistory();
  const locationUrl = useLocationUrl(locationId);

  const [activeAccordion, setActiveAccordion] = useState(true);

  const inspectionsListUrl = useInspectionsListUrl();

  const [isChangeLocationModalOpen, setIsChangeLocationModalOpen] = useState<boolean>(false);

  const queryParams = queryString.parse(window.location.search);
  const openScheduleModal = queryParams["schedule"] === "true";
  const [isScheduleInspectionModalOpen, setIsScheduleInspectionModalOpen] = useState<boolean>(openScheduleModal);
  const [isDeleteInspectionModalOpen, setIsDeleteInspectionModalOpen] = useState<boolean>(false);

  const handleCloseEditPrimaryLocationModal = () => {
    setIsChangeLocationModalOpen(false);
  };
  const handleOpenChangeLocationModal = () => {
    setIsChangeLocationModalOpen(true);
  };

  const handleOpenScheduleModal = () => {
    setIsScheduleInspectionModalOpen(true);
  };
  const handleCloseScheduleModal = () => {
    setIsScheduleInspectionModalOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteInspectionModalOpen(true);
  };
  const handleCloseDeleteModal = () => {
    setIsDeleteInspectionModalOpen(false);
  };

  const handleSuccessfulDelete = () => {
    handleCloseDeleteModal();
    history.push(inspectionsListUrl);
  };

  const handleInspectionStarted = () => {
    createInspectionStartedAt();
  };

  // When a section is selected, try and scroll the section into view on the checklist
  const handleSelectSection = (id: string) => {
    const elementId = InspectionChecklistUtils.getInspectionSectionElementId(id);
    const element = document.getElementById(elementId);
    if (element !== null) {
      element.scrollIntoView();
    }
  };

  const inspectionTypeId = (inspectionQuery.data?.data.attributes as any)?.["inspections-types-id"];
  const scheduledAtExists = scheduledAt && scheduledAt !== "none";

  const staffInstructions = inspectionQuery.data?.data.attributes.onSiteContactInstructions;

  const scheduledDateTime = inspectionQuery.data?.data.attributes["scheduled-at"];
  const scheduledDate = inspectionQuery.data?.data.attributes["scheduled-date"];
  const scheduledMeridiem = inspectionQuery.data?.data.attributes["scheduled-meridiem"];

  const canReviewPermits = permissionsSelectors.can("review_permits")();

  return (
    <>
      <InspectionStaffLocationTabChangeLocationModal
        open={isChangeLocationModalOpen}
        onClose={handleCloseEditPrimaryLocationModal}
        inspectionId={id}
        locationId={locationId}
      />
      <ScheduleInspectionModal
        open={isScheduleInspectionModalOpen}
        onClose={handleCloseScheduleModal}
        inspectionId={id}
        inspectionTypeId={inspectionTypeId}
      />
      <InspectionDeleteModal
        open={isDeleteInspectionModalOpen}
        onClose={handleCloseDeleteModal}
        onSuccess={handleSuccessfulDelete}
        id={id}
      />
      <Segment basic className='tw-pb-1 tw-pt-6'>
        <div className='tw-flex tw-justify-between'>
          <Header as='h2'>{inspectionTitle}</Header>
          <div className='tw-inline-block'>
            <InspectionPanelEllipsisPopup
              status={status}
              scheduled={scheduledAtExists}
              onRescheduleClick={handleOpenScheduleModal}
              onChangeLocationClick={handleOpenChangeLocationModal}
              onDeleteClick={handleOpenDeleteModal}
            />
          </div>
        </div>
        {canReviewPermits ? (
          <Link to={`/${site}/permits/review/${recordId}#details`}>
            <Header as='h5' color='blue' className='link-text'>
              {permitsTypeName} {confirmationNumber}
            </Header>
          </Link>
        ) : (
          <div>
            <Header as='h5'>
              {permitsTypeName} {confirmationNumber}
            </Header>
          </div>
        )}
        <InspectionPanelCompletedSegment id={id} />
        <Accordion styled className='tw-mb-4 tw-mt-4'>
          <Accordion.Title active={activeAccordion} onClick={() => setActiveAccordion(!activeAccordion)}>
            <Header as='h5'>
              <Icon name='dropdown' />
              Inspection Info
            </Header>
          </Accordion.Title>
          <Accordion.Content active={activeAccordion}>
            <div className='accordion-content'>
              {parcelNumber ? (
                <div className='inspection-info'>
                  <Icon>
                    <FontAwesomeIcon icon={faMapLocationDot} />
                  </Icon>
                  <a href={locationUrl} target='_blank' rel='noopener noreferrer'>
                    {parcelNumber}
                    <Icon className='tw-ml-2'>
                      <FontAwesomeIcon size='sm' icon={faArrowUpRightFromSquare} />
                    </Icon>
                  </a>
                </div>
              ) : (
                <></>
              )}

              <div className='inspection-info'>
                <Icon name='building outline' />
                {address ? address : <p>No address listed</p>}
              </div>
              <div className='inspection-info'>
                <Icon name='clipboard outline' />
                <p> {assignee || "No inspector listed"} </p>
              </div>
              <div className='inspection-info'>
                <Icon name='calendar outline' />
                <p style={{ marginRight: 8, marginBottom: 0 }}>
                  <InspectionScheduledDateDisplay
                    scheduledDate={scheduledDate}
                    scheduledDateTime={scheduledDateTime}
                    scheduledMeridiem={scheduledMeridiem}
                  />
                </p>
              </div>
              <InspectionPanelOnSiteContactInfo {...onSiteContactInfo} />
              {staffInstructions && (
                <>
                  <Header sub content='Staff Instructions' />
                  <div className='inspection-info tw-flex'>
                    <div>
                      <FontAwesomeIcon className='tw-mr-2' icon={faMessage} />
                    </div>
                    <div>{staffInstructions}</div>
                  </div>
                </>
              )}
              <Header sub content='applicant' />
              <div className='inspection-info'>
                <Icon name='user outline' />
                <p> {applicant} </p>
              </div>
            </div>
          </Accordion.Content>
        </Accordion>
        <InspectionPanelButton
          isLoading={inspectionQuery.isLoading || isStartedAtLoading}
          status={status}
          onComplete={openModal}
          onStart={handleInspectionStarted}
        />
        <InspectionPanelSectionsNavigation id={id} onSelectSection={handleSelectSection} />
      </Segment>
    </>
  );
};
